import { CandlestickData, PriceFormat } from 'lightweight-charts'
import store, { RootState } from '../../../store'
import { buyColor, candleUpColorAlpha, green, red } from '../../../assets/colors'
import { BLANK_SERIES_ID, candleSeriesOptions } from '../../../assets/constants'
import { calculateChange, toLocaleStringIn } from '../../../util/general'
import { extractColorFromDataPoint, formatIndicatorOutputValue } from '../../../util/chart'
import { LiveMarketTextSelector } from '../../common/LiveMarketText'
import { LiveTextSelector } from '../../common/LiveText'
import { getEffectiveChartSnapshotSeriesGroup, subscribeChartLiveData } from '../chartSnapshotStore'
import { SeriesData } from '../chart.typings'
import { selectChartSeriesGroups } from '../../../selectors/chart'
import { getChartBidAsk, subscribeChartBidAsk } from '../chartBidAskStore'
import { ChartType } from '../../../proto/generated/chart'
import tinycolor from 'tinycolor2'

const getChartInstance = (viewKey: string) => window.chartManager?.getChart(viewKey)

const displaySelectorCache = new Map<string, LiveTextSelector>()
const colorSelectorCache = new Map<string, LiveTextSelector>()
const reduxDisplayCache = new Map<string, LiveMarketTextSelector>()
const reduxColorCache = new Map<string, LiveMarketTextSelector>()

const getCachedLiveTextSelector = (
    cache: Map<string, LiveTextSelector>,
    cacheKey: string,
    factory: () => LiveTextSelector
): LiveTextSelector => {
    if (!cache.has(cacheKey)) {
        cache.set(cacheKey, factory())
    }
    return cache.get(cacheKey)!
}

const getCachedReduxSelector = (
    cache: Map<string, LiveMarketTextSelector>,
    cacheKey: string,
    factory: () => LiveMarketTextSelector
): LiveMarketTextSelector => {
    if (!cache.has(cacheKey)) {
        cache.set(cacheKey, factory())
    }
    return cache.get(cacheKey)!
}

const formatDisplayValue = (
    value: number,
    priceFormat: PriceFormat['type'],
    precision?: number
): string => formatIndicatorOutputValue(value, priceFormat, precision)

const getPrimaryPointValue = (point?: { value?: number; close?: number }): number =>
    point?.value ?? point?.close ?? 0

const getPrimarySnapshotPoints = (viewKey: string): Array<{ value?: number; close?: number }> => {
    const primary = getEffectiveChartSnapshotSeriesGroup(viewKey, 'primary')
    if (!primary) return []
    const chart = getChartInstance(viewKey)
    const primarySeriesId = chart?.getPrimarySeriesId()
    const blankSeriesId = chart?.primarySeriesGroup?.series[1]?.id ?? BLANK_SERIES_ID
    if (primarySeriesId && primary[primarySeriesId]) {
        return (primary[primarySeriesId] ?? []) as Array<{ value?: number; close?: number }>
    }
    const entry = Object.entries(primary).find(([seriesId]) => seriesId !== blankSeriesId && seriesId !== BLANK_SERIES_ID)
    const [, data] = entry ?? Object.entries(primary)[0] ?? []
    return (data ?? []) as Array<{ value?: number; close?: number }>
}

const getPrimaryCandles = (viewKey: string): CandlestickData[] =>
    getPrimarySnapshotPoints(viewKey) as CandlestickData[]

const getSeriesCandles = (viewKey: string, seriesGroupId: string, seriesId: string): CandlestickData[] => {
    const group = getEffectiveChartSnapshotSeriesGroup(viewKey, seriesGroupId)
    return (group?.[seriesId] ?? []) as CandlestickData[]
}

const toSolidDisplayColor = (color: string): string => {
    if (!color || color === 'inherit' || color === 'transparent') return color
    const parsed = tinycolor(color)
    return parsed.isValid() ? parsed.setAlpha(1).toHexString() : color
}

const isHistogramLikeChartType = (chartType: ChartType) =>
    chartType === ChartType.HISTOGRAM || chartType === ChartType.COLUMN

const isFixedSeriesColorChartType = (chartType: ChartType) =>
    chartType === ChartType.LINE ||
    chartType === ChartType.LINE_WITH_MARKERS ||
    chartType === ChartType.STEP_LINE_CHART ||
    chartType === ChartType.AREA

const getPrimarySeriesRenderingInfo = (viewKey: string): SeriesRenderingInfo | undefined => {
    const chart = getChartInstance(viewKey)
    const primarySeries = chart?.primarySeriesGroup?.series[0]
    if (!primarySeries) return undefined
    const chartSeries = chart?.primarySeriesGroup?.chartSeries?.[primarySeries.id]
    const liveOptions = chartSeries?.seriesApi?.options?.() as Record<string, unknown> | undefined
    return {
        chartType: primarySeries.chartType,
        options: {
            ...(primarySeries.options as Record<string, unknown> | undefined),
            ...(chartSeries?.seriesOptions as Record<string, unknown> | undefined),
            ...liveOptions,
        },
        configColor: primarySeries.color,
    }
}

const getUpDownColors = (chartType: ChartType, options?: Record<string, unknown>) => {
    switch (chartType) {
        case ChartType.HOLLOW_CANDLES:
            return {
                upColor: (options?.upColorHollow as string | undefined) ?? buyColor,
                downColor: (options?.downColorHollow as string | undefined) ?? red,
            }
        case ChartType.HISTOGRAM:
        case ChartType.COLUMN:
            return {
                upColor: (options?.upColor as string | undefined) ?? candleUpColorAlpha,
                downColor: (options?.downColor as string | undefined) ?? '#ef535080',
            }
        default:
            return {
                upColor: (options?.upColor as string | undefined) ?? candleSeriesOptions.upColor,
                downColor: (options?.downColor as string | undefined) ?? candleSeriesOptions.downColor,
            }
    }
}

type SeriesRenderingInfo = {
    chartType: ChartType
    options?: Record<string, unknown>
    configColor?: string
}

const getSeriesColorFallback = (chartType: ChartType, options?: Record<string, unknown>, configColor?: string): string => {
    switch (chartType) {
        case ChartType.LINE:
        case ChartType.LINE_WITH_MARKERS:
        case ChartType.STEP_LINE_CHART:
            return (options?.color as string | undefined) ?? configColor ?? buyColor
        case ChartType.AREA:
            return (
                (options?.lineColor as string | undefined) ??
                (options?.color as string | undefined) ??
                (options?.topColor as string | undefined) ??
                configColor ??
                buyColor
            )
        case ChartType.HIGH_LOW:
            return (options?.upColor as string | undefined) ?? configColor ?? buyColor
        default:
            return configColor ?? buyColor
    }
}

const getPrimarySeriesDisplayColor = (viewKey: string, candle?: SeriesData): string => {
    const rendering = getPrimarySeriesRenderingInfo(viewKey)
    const chartType = rendering?.chartType ?? ChartType.CANDLESTICK
    const options = rendering?.options

    if (isFixedSeriesColorChartType(chartType)) {
        return getSeriesColorFallback(chartType, options, rendering?.configColor)
    }

    if (chartType === ChartType.BASELINE) {
        const point = candle as { value?: number; close?: number } | undefined
        const val = point?.value ?? point?.close
        if (val == null) return 'inherit'
        const base = (options?.baseValue as number | undefined) ?? 0
        return val >= base
            ? ((options?.topLineColor as string | undefined) ?? buyColor)
            : ((options?.bottomLineColor as string | undefined) ?? red)
    }

    const pointColor = candle ? extractColorFromDataPoint(candle, chartType) : undefined
    if (pointColor) {
        return pointColor === 'transparent'
            ? ((candle as { customValues?: Record<string, unknown> })?.customValues?.['backup_color'] as string | undefined) ?? 'inherit'
            : pointColor
    }

    if (chartType === ChartType.HIGH_LOW) {
        return getSeriesColorFallback(chartType, options, rendering?.configColor)
    }

    return getHoveredCandleDisplayColor(candle, chartType, options)
}

const getHoveredCandleDisplayColor = (
    candle: SeriesData | undefined,
    chartType: ChartType,
    options?: Record<string, unknown>,
    solid = false
): string => {
    if (!candle) return 'inherit'
    const colorFromPoint = extractColorFromDataPoint(candle, chartType)
    if (colorFromPoint) return solid ? toSolidDisplayColor(colorFromPoint) : colorFromPoint

    const ohlc = candle as CandlestickData
    if (ohlc.open == null || ohlc.close == null) return 'inherit'

    const { upColor, downColor } = getUpDownColors(chartType, options)
    const color = ohlc.close >= ohlc.open ? upColor : downColor
    return solid ? toSolidDisplayColor(color) : color
}

const formatSigned = (value: number, decimals = 2): string => {
    const abs = Math.abs(value) || 0
    const sign = value > 0 ? '+' : value < 0 ? '-' : ''
    return `${sign}${abs.toFixed(decimals)}`
}

export const buildSeriesMetadataLiveSubscribe = (viewKey: string, seriesGroupIds: string[]) => {
    return (listener: () => void) => subscribeChartLiveData(viewKey, seriesGroupIds, listener)
}

export const buildChartBidAskSubscribe = (viewKey: string) => {
    return (listener: () => void) => subscribeChartBidAsk(viewKey, listener)
}

export const getChartTopBidDisplaySelector = (viewKey: string, priceMultiplier: number): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `chartTopBid:${viewKey}:${priceMultiplier}`, () => () => {
        const { topBid } = getChartBidAsk(viewKey)
        return toLocaleStringIn(topBid / priceMultiplier)
    })

export const getChartTopAskDisplaySelector = (viewKey: string, priceMultiplier: number): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `chartTopAsk:${viewKey}:${priceMultiplier}`, () => () => {
        const { topAsk } = getChartBidAsk(viewKey)
        return toLocaleStringIn(topAsk / priceMultiplier)
    })

export const getChartBidAskSpreadSelector = (viewKey: string, priceMultiplier: number): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `chartSpread:${viewKey}:${priceMultiplier}`, () => () => {
        const { topBid, topAsk } = getChartBidAsk(viewKey)
        return toLocaleStringIn(Math.abs(topAsk - topBid) / priceMultiplier)
    })

export const getIndicatorCustomLabelSelector = (
    viewKey: string,
    seriesGroupId: string,
    seriesId: string
): LiveTextSelector =>
    getCachedLiveTextSelector(
        displaySelectorCache,
        `indicatorLabel:${viewKey}:${seriesGroupId}:${seriesId}`,
        () => () => {
            const groupSnap = getEffectiveChartSnapshotSeriesGroup(viewKey, seriesGroupId)
            const data = groupSnap?.[seriesId]
            const lastCandle = data?.[(data?.length ?? 0) - 1] as { customValues?: Record<string, unknown> } | undefined
            return (lastCandle?.customValues?.label as string | undefined) ?? ''
        }
    )

export const getStrikeDisplaySelector = (viewKey: string, priceMultiplier: number): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `strike:${viewKey}:${priceMultiplier}`, () => () => {
        const primarySnap = getEffectiveChartSnapshotSeriesGroup(viewKey, 'primary')
        if (!primarySnap) return '-'
        const [, data] = Object.entries(primarySnap)[0] ?? []
        const lastCandle = data?.[(data?.length ?? 0) - 1] as { referencedSecurityCode?: string } | undefined
        const refCode = lastCandle?.referencedSecurityCode ?? ''
        const chart = getChartInstance(viewKey)
        const strikePrice = chart?.dataManager?.referenceSecurities?.[refCode]?.strikePrice
        return strikePrice != null ? toLocaleStringIn(strikePrice / priceMultiplier) : '-'
    })

export const getPrimaryOhlcColorSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(colorSelectorCache, `primaryOhlcColor:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        return getPrimarySeriesDisplayColor(viewKey, candles[candles.length - 1])
    })

export const getPrimaryValueSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `primaryValue:${viewKey}`, () => () => {
        const candles = getPrimarySnapshotPoints(viewKey)
        return toLocaleStringIn(getPrimaryPointValue(candles[candles.length - 1]))
    })

export const getPrimaryOpenSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `primaryOpen:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        return toLocaleStringIn(candles[candles.length - 1]?.open || 0)
    })

export const getPrimaryHighSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `primaryHigh:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        return toLocaleStringIn(candles[candles.length - 1]?.high || 0)
    })

export const getPrimaryLowSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `primaryLow:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        return toLocaleStringIn(candles[candles.length - 1]?.low || 0)
    })

export const getPrimaryCloseSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `primaryClose:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        return toLocaleStringIn(candles[candles.length - 1]?.close || 0)
    })

export const getPrimaryBarChangeSelector = (viewKey: string): LiveTextSelector =>
    getCachedLiveTextSelector(displaySelectorCache, `primaryBarChange:${viewKey}`, () => () => {
        const candles = getPrimarySnapshotPoints(viewKey)
        if (candles.length < 2) return '0.00 (0.00%)'
        const prev = getPrimaryPointValue(candles[0])
        const curr = getPrimaryPointValue(candles[1])
        const change = curr - prev
        const changePercent = (change * 100) / (prev || 1)
        return `${formatSigned(change)} (${formatSigned(changePercent)}%)`
    })

export const getDayChangeReduxSelectors = (
    chartSecurityKey: string,
    expressionMode: boolean
): { text: LiveMarketTextSelector; color: LiveMarketTextSelector } => {
    const prefix = `seriesMetaDayChg:${chartSecurityKey}:${expressionMode}`
    const text = getCachedReduxSelector(reduxDisplayCache, prefix, () => (state: RootState) => {
        const snap = state.securityStore.snapshots[chartSecurityKey]
        if (!snap) return ''
        const { change, changePercentage } = calculateChange(snap.lastTradedPrice, snap.previousClose)
        const changeVal = expressionMode ? change : change / 100
        return `${change > 0 ? '+' : ''}${changeVal.toFixed(2)} (${changePercentage > 0 ? '+' : ''}${changePercentage.toFixed(2)}%)`
    })
    const color = getCachedReduxSelector(reduxColorCache, `${prefix}:color`, () => (state: RootState) => {
        const snap = state.securityStore.snapshots[chartSecurityKey]
        if (!snap) return 'inherit'
        const { change } = calculateChange(snap.lastTradedPrice, snap.previousClose)
        if (change > 0) return green
        if (change < 0) return red
        return 'inherit'
    })
    return { text, color }
}

export const getEmbeddedDayChangeReduxSelectors = (
    viewKey: string,
    chartSecurityKey: string,
    expressionMode: boolean
): {
    ltpText: LiveTextSelector
    ltpColor: LiveTextSelector
    changeText: LiveMarketTextSelector
    changeColor: LiveMarketTextSelector
} => {
    const ltpText = getCachedLiveTextSelector(displaySelectorCache, `embedLtp:${viewKey}`, () => () => {
        const candles = getPrimarySnapshotPoints(viewKey)
        return toLocaleStringIn(getPrimaryPointValue(candles[candles.length - 1]))
    })
    const ltpColor = getCachedLiveTextSelector(colorSelectorCache, `embedLtpColor:${viewKey}`, () => () => {
        const candles = getPrimaryCandles(viewKey)
        const rendering = getPrimarySeriesRenderingInfo(viewKey)
        const chartType = rendering?.chartType ?? ChartType.CANDLESTICK
        const candleCol = getPrimarySeriesDisplayColor(viewKey, candles[candles.length - 1])
        if (isFixedSeriesColorChartType(chartType) || !chartSecurityKey) return candleCol
        const snap = store.getState().securityStore.snapshots[chartSecurityKey]
        if (!snap) return candleCol
        const { change } = calculateChange(snap.lastTradedPrice, snap.previousClose)
        if (change > 0) return green
        if (change < 0) return red
        return candleCol
    })

    const prefix = `seriesMetaEmbedChg:${chartSecurityKey}:${expressionMode}`
    const changeText = getCachedReduxSelector(reduxDisplayCache, prefix, () => (state: RootState) => {
        const snap = state.securityStore.snapshots[chartSecurityKey]
        if (!snap) return ''
        const { change, changePercentage } = calculateChange(snap.lastTradedPrice, snap.previousClose)
        const changeVal = expressionMode ? change : change / 100
        const sign = change > 0 ? '+' : change < 0 ? '-' : ''
        const pctSign = changePercentage > 0 ? '+' : changePercentage < 0 ? '-' : ''
        return `${sign}${Math.abs(changeVal).toFixed(2)} (${pctSign}${Math.abs(changePercentage).toFixed(2)}%)`
    })
    const changeColor = getCachedReduxSelector(reduxColorCache, `${prefix}:color`, () => (state: RootState) => {
        const snap = state.securityStore.snapshots[chartSecurityKey]
        if (!snap) return 'inherit'
        const { change } = calculateChange(snap.lastTradedPrice, snap.previousClose)
        if (change > 0) return green
        if (change < 0) return red
        return 'inherit'
    })

    return { ltpText, ltpColor, changeText, changeColor }
}

export const getComparableCloseSelector = (
    viewKey: string,
    seriesGroupId: string,
    seriesId: string,
    priceFormat: PriceFormat['type']
): LiveTextSelector =>
    getCachedLiveTextSelector(
        displaySelectorCache,
        `comparableClose:${viewKey}:${seriesGroupId}:${seriesId}:${priceFormat}`,
        () => () => {
            const candles = getSeriesCandles(viewKey, seriesGroupId, seriesId)
            const last = candles[candles.length - 1]
            return formatDisplayValue(last?.close || 0, priceFormat)
        }
    )

export const getIndicatorValueSelector = (
    viewKey: string,
    seriesGroupId: string,
    seriesId: string,
    priceFormat: PriceFormat['type'],
    chartType: string,
    staticColor?: string
): { text: LiveTextSelector; color: LiveTextSelector } => {
    const cacheKey = `indicator:${viewKey}:${seriesGroupId}:${seriesId}:${priceFormat}:${chartType}:${staticColor ?? ''}`
    const text = getCachedLiveTextSelector(displaySelectorCache, `${cacheKey}:text`, () => () => {
        const outputValuesPrecision = selectChartSeriesGroups(viewKey)(store.getState())?.[seriesGroupId]
            ?.outputValuesPrecision
        const candles = getSeriesCandles(viewKey, seriesGroupId, seriesId)
        const last = candles[candles.length - 1] as SeriesData & { value?: number; label?: string }
        let display = formatDisplayValue(last?.value || 0, priceFormat, outputValuesPrecision)
        if (last?.label) display += ` ${last.label}`
        return display
    })
    const color = getCachedLiveTextSelector(colorSelectorCache, `${cacheKey}:color`, () => () => {
        if (staticColor) return staticColor
        const candles = getSeriesCandles(viewKey, seriesGroupId, seriesId)
        const last = candles[candles.length - 1]
        const chart = getChartInstance(viewKey)
        const seriesConfig = chart?.chartConfig?.series?.[seriesId]
        const chartSeries = chart?.seriesGroupRef?.[seriesGroupId]?.chartSeries?.[seriesId]
        const resolvedChartType = (seriesConfig?.chartType ?? chartType) as ChartType
        return getHoveredCandleDisplayColor(
            last,
            resolvedChartType,
            (chartSeries?.seriesOptions ?? seriesConfig?.options) as Record<string, unknown> | undefined,
            isHistogramLikeChartType(resolvedChartType)
        )
    })
    return { text, color }
}
